import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';

export interface InstructorClass {
  id: string;
  type: string;
  modality: string;
  start_time: string;
  end_time: string;
  status: string;
  academy_id?: string;
  academy_name?: string;
  // Add more fields as needed
}

export const useInstructorClasses = (selectedDate?: Date) => {
  const { user } = useAuth();

  const { data: classes, isLoading, error, refetch } = useQuery({
    queryKey: ['instructor-classes', user?.id, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch instructor classes for the selected date only
      const selectedDateStart = new Date(selectedDate);
      selectedDateStart.setHours(0, 0, 0, 0);
      
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      
      const { data, error } = await supabase
        .from('trainings')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_class', true)
        .gte('start_time', selectedDateStart.toISOString())
        .lte('start_time', selectedDateEnd.toISOString())
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching instructor classes:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    classes: classes || [],
    isLoading,
    error,
    refetch,
  };
};
